import { BannerExtension } from "./extension";
import {
  BannerSettings,
  BannerTexts,
  ConsentAction,
  PublicCookie,
} from "./types";

/** What the preference center (bc-prefs.js) uses of the banner: its data and its building blocks. */
export interface PreferencesHost {
  readonly doc: Document;
  readonly settings: BannerSettings;
  readonly texts: BannerTexts;
  readonly cookies: PublicCookie[];
  readonly language: string;
  readonly extension: BannerExtension | null;
  el(tag: string, className: string, text?: string): HTMLElement;
  button(
    label: string,
    className: string,
    onClick: () => void,
  ): HTMLButtonElement;
  buttonRow(): HTMLElement;
  rejectClass(): string;
  acceptClass(): string;
  all(): string[];
  required(): string[];
  decide(
    action: ConsentAction,
    categories: string[],
    fromSwitches?: boolean,
  ): void;
  policyUrl(): string | null;
  privacyLink(): HTMLAnchorElement;
  addBranding(dialog: HTMLElement): void;
  present(element: HTMLElement, modal: boolean): void;
  /** Adds styles to the shadow root once. */
  addStyle(css: string): void;
}

export interface PreferencesModule {
  render(host: PreferencesHost, accepted?: string[], consentId?: string): void;
}

declare global {
  interface Window {
    __berrycookiePrefs?: PreferencesModule;
  }
}

/** Adds a module script that sits next to bc.js; resolves to false when it cannot be loaded. */
export function loadScript(
  doc: Document,
  scriptUrl: string | null,
  file: string,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (!scriptUrl) {
      resolve(false);
      return;
    }
    const script = doc.createElement("script");
    script.src = new URL(file, scriptUrl).href;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    (doc.head ?? doc.documentElement).appendChild(script);
  });
}

let loading: Promise<PreferencesModule | null> | null = null;

/**
 * Loads bc-prefs.js (the preference center) from where bc.js came from, once. Resolves to null when it cannot be
 * loaded; the banner then falls back to its first layer, which still offers "Reject all" and "Accept all".
 */
export function loadPreferences(
  win: Window,
  doc: Document,
  scriptUrl: string | null,
): Promise<PreferencesModule | null> {
  if (win.__berrycookiePrefs) {
    return Promise.resolve(win.__berrycookiePrefs);
  }
  loading ??= loadScript(doc, scriptUrl, "bc-prefs.js").then((loaded) => {
    if (!loaded) {
      // Try again on the next attempt (the visitor may have been offline for a moment).
      loading = null;
      console.warn("[BerryCookie] bc-prefs.js could not be loaded.");
    }
    return win.__berrycookiePrefs ?? null;
  });
  return loading;
}
